/**
 * Canonical Login Flow Implementation
 * 
 * Login is successful ONLY when BOTH conditions are met:
 * 1. MSAL authentication succeeds (valid ID token / access token obtained, account set in MSAL cache)
 * 2. UserService returns a valid, active user record (user exists in DB, user is active/allowed, role & tenant context resolved)
 * 
 * If either fails → login fails.
 */

import type { AccountInfo } from '@azure/msal-browser';
import { UserProfileApi } from '../api/userProfile';
import { setCachedProfile } from './userProfileCache.utils';
import { classifyLoginError, LoginErrorType, type LoginError } from './loginError.utils';
import type { User } from '../context/AuthContext';
import { normalizeProfileRoles } from './profileRoles.utils';
import { AuthIdentityApi, type UserIdentityDto } from '../api/authIdentity';

export interface CompleteLoginResult {
  success: boolean;
  user?: User;
  userProfile?: any; // UserProfile from UserService
  error?: LoginError;
}

/**
 * Complete login flow: MSAL + UserService validation
 * 
 * @param accessToken - MSAL access token (already validated by MSAL)
 * @param account - MSAL account info
 * @returns CompleteLoginResult with success status and user data or error
 */
export async function completeLogin(
  accessToken: string,
  account: AccountInfo
): Promise<CompleteLoginResult> {
  try {
    // Step 1: Validate access token (should already be validated, but double-check)
    if (!accessToken) {
      return {
        success: false,
        error: {
          type: LoginErrorType.AUTH_FAILURE,
          message: 'No access token in MSAL result',
          userFacingMessage: "We couldn't complete sign-in. Please try again.",
          action: 'retry',
        },
      };
    }

    // Step 2: Extract Entra Object ID from account
    const entraObjectId =
      account.localAccountId ||
      account.homeAccountId?.split('.')[0] ||
      (account.idTokenClaims?.oid as string) ||
      (account.idTokenClaims?.sub as string) ||
      null;

    if (!entraObjectId) {
      return {
        success: false,
        error: {
          type: LoginErrorType.AUTH_FAILURE,
          message: 'Could not extract Entra Object ID from account',
          userFacingMessage: "We couldn't complete sign-in. Please try again.",
          action: 'retry',
        },
      };
    }

    // Step 3: Validate identity via AuthService first (authoritative for userId, isActive, roles).
    let identity: UserIdentityDto;
    try {
      console.log('[completeLogin] AuthService identity fetch, entraObjectId:', entraObjectId);
      identity = await AuthIdentityApi.getEntraIdentity();
      console.log('[completeLogin] AuthService identity confirmed, userId:', identity?.userId);
    } catch (fetchError: unknown) {
      console.error('[completeLogin] AuthService identity fetch failed:', {
        message: fetchError instanceof Error ? fetchError.message : String(fetchError),
        entraObjectId,
      });
      return {
        success: false,
        error: classifyLoginError(fetchError),
      };
    }

    // Step 4: Validate identity (AuthService is the authority for userId, isActive, and roles).
    if (!identity?.userId) {
      return {
        success: false,
        error: {
          type: LoginErrorType.USER_NOT_AUTHORIZED,
          message: 'AuthService returned no userId for this identity',
          userFacingMessage: "We couldn't complete sign-in. You're signed in, but your account doesn't have required permissions. Please contact your administrator.",
          action: 'block',
        },
      };
    }

    // AuthService already returns 404 for inactive users (UserController.cs:43-44),
    // so identity.isActive is always true here. No separate check needed.

    // Step 5: Fetch demographics profile from UserService only after AuthService confirms the user.
    let userProfile;
    try {
      console.log('[completeLogin] UserService profile fetch, entraObjectId:', entraObjectId);
      userProfile = await UserProfileApi.getByEntraObjectId(entraObjectId);
      console.log('[completeLogin] UserService profile fetch complete:', userProfile ? 'found' : 'not found');
    } catch (fetchError: unknown) {
      console.error('[completeLogin] UserService profile fetch failed:', {
        message: fetchError instanceof Error ? fetchError.message : String(fetchError),
        entraObjectId,
      });
      return {
        success: false,
        error: classifyLoginError(fetchError),
      };
    }

    // Step 6: Validate demographics profile from UserService.
    if (!userProfile) {
      console.error('[completeLogin] UserService returned null user profile');
      return {
        success: false,
        error: {
          type: LoginErrorType.USER_NOT_PROVISIONED,
          message: 'UserService returned null user profile',
          userFacingMessage: "We couldn't complete sign-in. You're signed in, but your account isn't set up for this system. Please contact support to request access.",
          action: 'support',
        },
      };
    }

    // Warm encrypted profile cache with UserService demographics (roles not included).
    try {
      await setCachedProfile(entraObjectId, userProfile);
    } catch (cacheErr) {
      console.warn('[completeLogin] Failed to set cached profile (non-fatal):', cacheErr);
    }

    // Step 7: Build User object for AuthContext.
    // Roles come from AuthService (authoritative); demographics come from UserService.
    const normalizedRoles = normalizeProfileRoles(identity.roles);
    const user: User = {
      id: entraObjectId,
      profileUserId: String(identity.userId),
      email: userProfile.emailAddress || account.username || '',
      name: userProfile.firstName && userProfile.lastName
        ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
        : userProfile.userName || account.name || account.username || '',
      roles: normalizedRoles,
      role: normalizedRoles.length > 0 ? normalizedRoles[0] : undefined,
    };

    // Step 8: Return success
    return {
      success: true,
      user,
      userProfile,
    };
  } catch (error: any) {
    // Catch-all for unexpected errors
    const loginError = classifyLoginError(error);
    
    return {
      success: false,
      error: loginError,
    };
  }
}
